import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useAxios } from '@/contexts/AxiosProvider'
import { useHall } from '@/contexts/HallProvider'
import { useHallUsers } from '@/contexts/HallUsersProvider'
import { Evidence } from '@db8/shared'

const evidenceSchema = z
  .object({
    title: z.string().min(1, 'Title is required.'),
    link: z.union([z.string(), z.literal(''), z.null()]).optional(),
    summary: z.string().min(1, 'Summary is required.'),
    content: z.string().optional().or(z.literal('')),
  })
  .refine((data) => (data.link?.trim?.() || '').length > 0 || data.content.trim().length > 0, {
    message: 'Either a link or full content is required.',
    path: ['link'],
  })

export const EvidenceSubmissionModal = ({
  showSubmissionForm,
  setShowSubmissionForm,

  initialData = null,
  onSubmitComplete = (newEvidence: Evidence) => {},
  onClose = () => {},
}) => {
  const isEditing = !!initialData
  const axios = useAxios()
  const { hall } = useHall()
  const { currentUser } = useHallUsers()

  const form = useForm({
    resolver: zodResolver(evidenceSchema),
    defaultValues: initialData || {
      title: '',
      link: '',
      summary: '',
      content: '',
    },
  })

  const onSubmit = async (data) => {
    try {
      let newEvidence
      if (isEditing) {
        let res = await axios.put(`/evidence/${initialData.id}`, data)
        newEvidence = res.data
      } else {
        let res = await axios.post('/evidence', {
          ...data,
          hallId: hall.id,
          speakingRole: currentUser.speakingRole,
        })
        newEvidence = res.data
      }

      onClose()
      onSubmitComplete(newEvidence)
    } catch (err) {
    }
  }
  return (
    <Dialog
      open={showSubmissionForm}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        size="md"
        className="dark:text-white max-h-[90vh] md:min-w-[600px] w-full max-w-2xl flex flex-col"
      >
        <DialogTitle className="text-xl font-bold">
          {isEditing ? 'Edit Evidence' : 'Submit New Evidence'}
        </DialogTitle>
        <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-1">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pt-4">
              <FormField
                name="title"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter a title..."
                        className="w-full dark:bg-gray-800 border border-gray-700"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="link"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Link (optional)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="https://example.com"
                        className="w-full dark:bg-gray-800 border border-gray-700"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="summary"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Summary</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="One-line summary"
                        maxLength={100}
                        className="w-full dark:bg-gray-800 border border-gray-700"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="content"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Content</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={6}
                        placeholder="Paste full article or summary..."
                        className="w-full dark:bg-gray-800 border border-gray-700"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end">
                <Button type="submit" variant="outline" disabled={form.formState.isSubmitting}>
                  Submit
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
